import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CreateFeedbackRequest, FeedbackType } from 'features/feedback/shared/feedback.types';

export type FeedbackStep = 'type' | 'bug' | 'review' | 'idea' | 'success';
export type FeedbackFormState = 'idle' | 'submitting' | 'error' | 'success';

interface Slice {
  drawerOpen: boolean;
  step: FeedbackStep;
  currentType: FeedbackType | null;
  formState: FeedbackFormState;
  submittedType: FeedbackType | null;
  errorMessage?: string;
}

const getInitialState = (): Slice => ({
  drawerOpen: false,
  step: 'type',
  currentType: null,
  formState: 'idle',
  submittedType: null,
});

const stepForType: Record<FeedbackType, FeedbackStep> = {
  BUG: 'bug',
  REVIEW: 'review',
  IDEA: 'idea',
};

const feedbackSlice = createSlice({
  name: 'feedback',
  initialState: getInitialState(),
  reducers: {
    openDrawer: (s) => {
      s.drawerOpen = true;
      s.step = 'type';
      s.currentType = null;
      s.formState = 'idle';
      s.submittedType = null;
      s.errorMessage = undefined;
    },
    closeDrawer: (s) => {
      s.drawerOpen = false;
      s.formState = 'idle';
    },
    pickType: (s, { payload }: PayloadAction<FeedbackType>) => {
      s.currentType = payload;
      s.step = stepForType[payload];
      s.formState = 'idle';
    },
    back: (s) => {
      s.step = 'type';
      s.currentType = null;
      s.formState = 'idle';
    },
    submit: (s, _action: PayloadAction<CreateFeedbackRequest>) => {
      s.formState = 'submitting';
      s.errorMessage = undefined;
    },
    submitSuccess: (s) => {
      s.formState = 'success';
      s.step = 'success';
      s.submittedType = s.currentType;
    },
    submitError: (s, { payload }: PayloadAction<string | undefined>) => {
      s.formState = 'error';
      s.errorMessage = payload;
    },
    reset: () => getInitialState(),
  },
});

export default feedbackSlice;
export const feedbackReducer = feedbackSlice.reducer;
export const feedbackActions = feedbackSlice.actions;
